import type { AuthenticatedUser } from '../api/types'

const storageKey = 'repinu-offline-session-v1'
const maximumOfflineAgeMs = 36 * 60 * 60 * 1000

type OfflineSessionCache = {
  schemaVersion: 1
  verifiedAt: string
  user: AuthenticatedUser
}

function isUser(value: unknown): value is AuthenticatedUser {
  if (!value || typeof value !== 'object') return false
  const user = value as Partial<AuthenticatedUser>
  return typeof user.id === 'string' &&
    user.id.length > 0 &&
    typeof user.username === 'string' &&
    user.username.length > 0 &&
    typeof user.role === 'string' &&
    (user.expiresAt === null || typeof user.expiresAt === 'string')
}

export function cacheVerifiedSession(user: AuthenticatedUser, now = new Date()): void {
  try {
    const value: OfflineSessionCache = {
      schemaVersion: 1,
      verifiedAt: now.toISOString(),
      user,
    }
    localStorage.setItem(storageKey, JSON.stringify(value))
  } catch {
    // A restricted browser may reject local storage; online auth remains available.
  }
}

export function readVerifiedOfflineSession(now = new Date()): AuthenticatedUser | null {
  try {
    const raw = localStorage.getItem(storageKey)
    if (!raw) return null
    const value = JSON.parse(raw) as Partial<OfflineSessionCache>
    const verifiedAt = typeof value.verifiedAt === 'string' ? Date.parse(value.verifiedAt) : Number.NaN
    const sessionExpiresAt = value.user?.expiresAt ? Date.parse(value.user.expiresAt) : Number.POSITIVE_INFINITY
    if (value.schemaVersion !== 1 ||
      !isUser(value.user) ||
      !Number.isFinite(verifiedAt) ||
      now.getTime() - verifiedAt > maximumOfflineAgeMs ||
      sessionExpiresAt <= now.getTime()) {
      localStorage.removeItem(storageKey)
      return null
    }
    return value.user
  } catch {
    try { localStorage.removeItem(storageKey) } catch { /* no-op */ }
    return null
  }
}

export function clearVerifiedOfflineSession(): void {
  try { localStorage.removeItem(storageKey) } catch { /* no-op */ }
}